import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { KeyRound, Loader2 } from 'lucide-react'
import { useChangeUserPassword } from '../hooks'
import type { User } from '@/types/entities'

interface ChangePasswordModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    user: User
    currentUserId?: number | null
}

export function ChangePasswordModal({ open, onOpenChange, user, currentUserId }: ChangePasswordModalProps) {
    const { t } = useTranslation()
    const [currentPassword, setCurrentPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const changePasswordMutation = useChangeUserPassword()
    const isChangingSelf = currentUserId != null && user.id === currentUserId

    const handleClose = () => {
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
        onOpenChange(false)
    }

    const handleSubmit = async () => {
        if (!newPassword || newPassword !== confirmPassword || newPassword.length < 8) return
        if (isChangingSelf && !currentPassword) return
        try {
            await changePasswordMutation.mutateAsync({
                id: user.id,
                newPassword,
                currentPassword: isChangingSelf ? currentPassword : undefined,
            })
            handleClose()
        } catch {
            // Toast handled by mutation
        }
    }

    const isValid =
        newPassword.length >= 8 &&
        newPassword === confirmPassword &&
        (!isChangingSelf || currentPassword.length > 0)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <KeyRound className="h-5 w-5" />
                        {t('userForm.changePassword')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('userForm.changePasswordDescription')}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 pt-2">
                    {isChangingSelf && (
                        <div className="space-y-2">
                            <Label htmlFor="change-pw-current">{t('userForm.currentPassword')}</Label>
                            <Input
                                id="change-pw-current"
                                type="password"
                                placeholder={t('userForm.enterPassword')}
                                autoComplete="current-password"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                            />
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="change-pw-new">{t('userForm.newPassword')}</Label>
                        <Input
                            id="change-pw-new"
                            type="password"
                            placeholder={t('userForm.newPasswordPlaceholder')}
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="change-pw-confirm">{t('userForm.confirmPassword')}</Label>
                        <Input
                            id="change-pw-confirm"
                            type="password"
                            placeholder={t('userForm.enterConfirmPassword')}
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>
                    {newPassword && confirmPassword && newPassword !== confirmPassword && (
                        <p className="text-sm text-destructive">{t('userForm.passwordsMustMatch')}</p>
                    )}
                </div>
                <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={handleClose} disabled={changePasswordMutation.isPending}>
                        {t('userForm.cancel')}
                    </Button>
                    <Button
                        disabled={!isValid || changePasswordMutation.isPending}
                        onClick={handleSubmit}
                    >
                        {changePasswordMutation.isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('userForm.updating')}
                            </>
                        ) : (
                            t('userForm.changePasswordButton')
                        )}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}
